import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
  MemberDeleteOptions,
  Mutation,
  MutationAddVisitorArgs,
  MutationAddVisitorGroupArgs,
  MutationDeleteVisitorByIdArgs,
  MutationDeleteVisitorGroupByIdArgs,
  MutationSendInvitationForMobileDeviceByIdArgs,
  MutationSetVisitorByIdArgs,
  MutationUnregisterMobileDeviceByIdArgs,
  MutationUpdateVisitorGroupArgs,
  MutationUpdateVisitorGroupDetailsArgs,
  Query,
  QueryGetVisitorByIdArgs,
  QueryGetVisitorGroupsForTenantArgs,
  VisitorGroup,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import {
  ADD_VISITOR_GROUP_V2,
  ADD_VISITOR_WITH_ROLE,
  DELETE_VISITOR_BY_ID,
  DELETE_VISITOR_GROUP_BY_ID,
  SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID,
  SET_VISITOR_BY_ID_WITH_ROLE,
  UNREGISTER_MOBILE_DEVICE_BY_ID,
  UPDATE_VISITOR_GROUP_DETAILS_V2,
  UPDATE_VISITOR_GROUP_V2
} from 'graphql/v2/mutations';
import {
  GET_ADMIN_VISITOR_GROUPS_FOR_TENANT,
  GET_ALL_VISITORS_GROUPS,
  GET_ALL_VISITORS_VISITOR_GROUPS,
  GET_VISITOR_DETAILS,
  GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE
} from 'graphql/v2/queries';
import { updateVisitorGroupMembershipWithAdminGroup } from 'hooks/allVisitors/drawers/utils';
import { useEffect } from 'react';
import { getAllAccessGrantsOfVisitorGroup, VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { TranslatedHookProps } from '../hooks.types';

const useExternalUsers = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const {
    data: externalUsersData,
    loading: loadingWithRole,
    error: externalUsersError
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>, QueryGetVisitorGroupsForTenantArgs>(
    GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
    {
      variables: { filter: { type: VisitorGroupType.CoContractors } }
    }
  );

  useEffect(() => {
    if (externalUsersError) handleFetchError(translation.external_groups_error_fetching);
  }, [externalUsersError]);

  const externalUsersGroups = externalUsersData?.getVisitorGroupsForTenant ?? [];

  const [fetchVisitorDetails] = useLazyQuery<Pick<Query, 'getVisitorById'>, QueryGetVisitorByIdArgs>(
    GET_VISITOR_DETAILS
  );

  const [fetchAdministratorsVisitorGroups] = useLazyQuery<
    Pick<Query, 'getVisitorGroupsForTenant'>,
    QueryGetVisitorGroupsForTenantArgs
  >(GET_ALL_VISITORS_VISITOR_GROUPS);

  const [fetchInstallersVisitorGroups] = useLazyQuery<
    Pick<Query, 'getVisitorGroupsForTenant'>,
    QueryGetVisitorGroupsForTenantArgs
  >(GET_ALL_VISITORS_VISITOR_GROUPS);

  const [
    addVisitorGroupWithMaxNumberOfMembersMutation,
    { loading: addVisitorGroupWithMaxNumberOfMembersMutationLoading }
  ] = useMutation<Pick<Mutation, 'addVisitorGroup'>, MutationAddVisitorGroupArgs>(ADD_VISITOR_GROUP_V2, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE, GET_ALL_VISITORS_GROUPS],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.external_groups_error_adding)
  });

  const [
    updateVisitorGroupWithMaxNumberOfMembersMutation,
    { loading: updateVisitorGroupWithMaxNumberOfMembersMutationLoading }
  ] = useMutation<Pick<Mutation, 'updateVisitorGroup'>, MutationUpdateVisitorGroupArgs>(UPDATE_VISITOR_GROUP_V2, {
    refetchQueries: (result) => [
      {
        query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
        variables: { filter: { id: result.data?.updateVisitorGroup?.id } }
      }
    ],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.external_groups_error_updating)
  });

  const [
    deleteVisitorGroupByIdMutation,
    { error: deleteVisitorGroupByIdMutationError, loading: deleteVisitorGroupByIdMutationLoading }
  ] = useMutation<Pick<Mutation, 'deleteVisitorGroupById'>, MutationDeleteVisitorGroupByIdArgs>(
    DELETE_VISITOR_GROUP_BY_ID,
    {
      refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE, GET_ALL_VISITORS_GROUPS],
      awaitRefetchQueries: true,
      onError: (error) => handleFetchError(error.message)
    }
  );

  const [addVisitorMemberWithRoleMutation, { loading: addVisitorMemberMutationWithRoleLoading }] = useMutation<
    Pick<Mutation, 'addVisitor'>,
    MutationAddVisitorArgs
  >(ADD_VISITOR_WITH_ROLE, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE],
    awaitRefetchQueries: true,
    onError: (error) => {
      console.error(error);
      switch (error.message) {
        case 'Visitor already exists':
          handleFetchError(translation.user_email_already_exists);
          break;
        case 'Installers cannot have expiration date.':
          handleFetchError(translation.expiration_date_error_installer);
          break;
        default:
          handleFetchError(translation.external_groups_user_error_adding);
      }
    }
  });

  const [
    setVisitorByIdWithRoleMutation,
    { error: setVisitorByIdWithRoleMutationError, loading: updateVisitorMemberByIdWithRoleLoading }
  ] = useMutation<Pick<Mutation, 'setVisitorById'>, MutationSetVisitorByIdArgs>(SET_VISITOR_BY_ID_WITH_ROLE, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE, GET_ADMIN_VISITOR_GROUPS_FOR_TENANT],
    awaitRefetchQueries: true,
    onError: (error) => {
      console.error(error.message);
      if (error.message.includes('Installers cannot have expiration date'))
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_updating);
    }
  });

  const [deleteVisitorByIdMutation, { loading: deleteVisitorByIdMutationLoading }] = useMutation<
    Pick<Mutation, 'deleteVisitorById'>,
    MutationDeleteVisitorByIdArgs
  >(DELETE_VISITOR_BY_ID, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.external_groups_user_error_deleting)
  });

  const [sendInvitationForMobileDeviceByIdMutation, { loading: sendInvitationForMobileDeviceByIdMutationLoading }] =
    useMutation<Pick<Mutation, 'sendInvitationForMobileDeviceById'>, MutationSendInvitationForMobileDeviceByIdArgs>(
      SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID,
      {
        onError: (error) => {
          if (
            error.message.includes('Too many requests') ||
            error.message === 'MobileDevice does not have registration token'
          )
            handleFetchError(translation.unable_to_send_invite_please_wait_10_minutes_before_trying_again);
          else handleFetchError(translation.error_while_resending_invitation);
        }
      }
    );

  const [unregisterMobileDeviceByIdMutation, { loading: unregisterMobileDeviceByIdMutationLoading }] = useMutation<
    Pick<Mutation, 'unregisterMobileDeviceById'>,
    MutationUnregisterMobileDeviceByIdArgs
  >(UNREGISTER_MOBILE_DEVICE_BY_ID, {
    refetchQueries: [GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE, GET_VISITOR_DETAILS],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.error_while_unregister_mobile_device)
  });

  const addVisitorGroupWithMaxNumberOfMembers = async (name: string, maxNumberOfMembers: number | null) => {
    const { data } = await addVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, maxNumberOfMembers, type: VisitorGroupType.CoContractors }
    });
    return data;
  };

  const updateVisitorGroupWithMaxNumberOfMembers = async (
    name: string,
    visitorGroupId: string,
    maxNumberOfMembers: number | null
  ) => {
    const { data } = await updateVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, visitorGroupId, maxNumberOfMembers }
    });
    return data;
  };

  const deleteVisitorGroupById = async (visitorGroupId: string, options: MemberDeleteOptions) => {
    const { data } = await deleteVisitorGroupByIdMutation({
      variables: { visitorGroupId, options }
    });
    return data;
  };

  type AddOrEditVisitorArgs = {
    name: string;
    visitorGroupMembershipInput: VisitorGroupMembershipInput[];
    shouldBeAdmin: boolean;
    email?: string;
    id?: string;
    expirationDate?: dayjs.Dayjs;
  };

  const addOrEditVisitor = async ({
    name,
    shouldBeAdmin,
    email,
    id,
    expirationDate,
    visitorGroupMembershipInput
  }: AddOrEditVisitorArgs) => {
    const [adminGroupData, installerGroupData, userDetails] = await Promise.all([
      fetchAdministratorsVisitorGroups({ variables: { filter: { type: VisitorGroupType.Administrators } } }),
      fetchInstallersVisitorGroups({ variables: { filter: { type: VisitorGroupType.Installers } } }),
      id ? fetchVisitorDetails({ variables: { id } }) : undefined
    ]);

    if (
      adminGroupData.error ||
      installerGroupData.error ||
      userDetails?.error ||
      adminGroupData.data?.getVisitorGroupsForTenant === undefined ||
      installerGroupData.data?.getVisitorGroupsForTenant === undefined ||
      (id && userDetails?.data?.getVisitorById === undefined)
    ) {
      handleFetchError(translation.external_groups_user_error_updating);
      return undefined;
    }

    const isVisitorInstallerAlready = installerGroupData.data.getVisitorGroupsForTenant.some((group) =>
      group.members.some((member) => member.email === email)
    );
    const isUserExistAlready = userDetails?.data?.getVisitorById;
    const expirationDateIso = expirationDate ? dayjs(expirationDate).toISOString() : undefined;
    const membershipWithExpirationDate = expirationDateIso
      ? (visitorGroupMembershipInput || []).map((membership) => ({ ...membership, expirationDate: expirationDateIso }))
      : visitorGroupMembershipInput || [];
    const updatedVisitorGroupMembership: VisitorGroupMembershipInput[] = updateVisitorGroupMembershipWithAdminGroup({
      visitorGroupMembership: membershipWithExpirationDate,
      options: {
        shouldBeAdmin,
        adminGroup: adminGroupData.data.getVisitorGroupsForTenant.find(
          (group) => group.type === VisitorGroupType.Administrators
        ),
        shouldBeInstaller: isVisitorInstallerAlready,
        installerGroup: installerGroupData.data.getVisitorGroupsForTenant.find(
          (group) => group.type === VisitorGroupType.Installers
        )!
      }
    });

    if (!isUserExistAlready && !isVisitorInstallerAlready) {
      const { data } = await addVisitorMemberWithRoleMutation({
        variables: {
          name,
          email,
          visitorGroupMembership: updatedVisitorGroupMembership
        }
      });
      return data?.addVisitor;
    } else if (id || isVisitorInstallerAlready) {
      const installerGroup = installerGroupData.data.getVisitorGroupsForTenant.find((group) => group);
      const installerId = installerGroup?.members.find((member) => member.email === email)?.id;
      const { data } = await setVisitorByIdWithRoleMutation({
        variables: {
          visitorId: id || installerId || '',
          name,
          visitorGroupMembership: updatedVisitorGroupMembership
        }
      });
      return data?.setVisitorById;
    }
    return undefined;
  };

  const deleteVisitorById = async (visitorId: string) => {
    const { data } = await deleteVisitorByIdMutation({ variables: { visitorId } });
    return data;
  };

  const sendInvitationForMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await sendInvitationForMobileDeviceByIdMutation({ variables: { visitorId, mobileDeviceId } });
    return data;
  };

  const unregisterMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await unregisterMobileDeviceByIdMutation({ variables: { visitorId, mobileDeviceId } });
    return data;
  };

  const [updateVisitorGroupDetailsMutation, { loading: updateVisitorGroupDetailsLoading }] = useMutation<
    Pick<Mutation, 'updateVisitorGroupDetails'>,
    MutationUpdateVisitorGroupDetailsArgs
  >(UPDATE_VISITOR_GROUP_DETAILS_V2, {
    refetchQueries: (result) => [
      { query: GET_ALL_VISITORS_GROUPS, variables: { filter: { id: result.data?.updateVisitorGroupDetails?.id } } }
    ],
    awaitRefetchQueries: true
  });

  type UpdateVisitorGroupDetailsArg = {
    visitorGroup: VisitorGroup;
    name?: string;
    // undefined = leave unchanged, null = clear the limit (must be sent explicitly, omitting the field does not clear it)
    maxNumberOfMembers?: number | null;
    isOfficeModeEnabled: boolean;
    isQrCodeVisitInviter: boolean;
  };

  const updateVisitorGroupDetails = async ({
    visitorGroup,
    name,
    maxNumberOfMembers,
    isOfficeModeEnabled,
    isQrCodeVisitInviter
  }: UpdateVisitorGroupDetailsArg): Promise<{ success: boolean }> => {
    const accessGrantId = getAllAccessGrantsOfVisitorGroup(visitorGroup)[0]?.id;
    if (!accessGrantId) {
      handleFetchError(translation.user_groups_error_updating);
      return { success: false };
    }

    try {
      const result = await updateVisitorGroupDetailsMutation({
        variables: {
          input: {
            visitorGroupId: visitorGroup.id,
            name,
            maxNumberOfMembers,
            isQrCodeVisitInviter,
            officeMode: {
              accessGrantId,
              isOfficeModeEnabled
            }
          }
        }
      });
      if (!result.data?.updateVisitorGroupDetails?.id) {
        handleFetchError(translation.user_groups_error_updating);
        return { success: false };
      }
      return { success: true };
    } catch (err) {
      handleFetchError(translation.user_groups_error_updating);
      console.error(err);
      return { success: false };
    }
  };

  const [setVisitorInfoByIdWithRoleMutation, { loading: setVisitorByIdWithRoleLoading }] = useMutation<
    Pick<Mutation, 'setVisitorById'>,
    MutationSetVisitorByIdArgs
  >(SET_VISITOR_BY_ID_WITH_ROLE, {
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS }],
    awaitRefetchQueries: true
  });

  type SetVisitorByIdWithRoleArg = {
    visitorId: string;
    name: string;
    visitorGroupMembership: VisitorGroupMembershipInput[];
  };

  const setVisitorByIdWithRole = async ({ visitorId, name, visitorGroupMembership }: SetVisitorByIdWithRoleArg) => {
    try {
      await setVisitorInfoByIdWithRoleMutation({
        variables: { visitorId, name, visitorGroupMembership }
      });
    } catch (err) {
      console.error(err);
      if (err instanceof Error && err.message.includes('Installers cannot have expiration date')) {
        handleFetchError(translation.expiration_date_error_installer);
      } else {
        handleFetchError(translation.external_groups_user_error_updating);
      }
      throw err;
    }
  };

  return {
    externalUsersGroups,
    loadingWithRole,
    addVisitorGroupWithMaxNumberOfMembers,
    addVisitorGroupWithMaxNumberOfMembersMutationLoading,
    updateVisitorGroupWithMaxNumberOfMembers,
    updateVisitorGroupWithMaxNumberOfMembersMutationLoading,
    deleteVisitorGroupById,
    deleteVisitorGroupByIdMutationError,
    deleteVisitorGroupByIdMutationLoading,
    addOrEditVisitor,
    addVisitorMemberMutationWithRoleLoading,
    updateVisitorMemberByIdWithRoleLoading,
    setVisitorByIdWithRoleMutationError,
    deleteVisitorById,
    deleteVisitorByIdMutationLoading,
    sendInvitationForMobileDeviceById,
    sendInvitationForMobileDeviceByIdMutationLoading,
    unregisterMobileDeviceById,
    unregisterMobileDeviceByIdMutationLoading,
    setVisitorByIdWithRole,
    setVisitorByIdWithRoleLoading,
    updateVisitorGroupDetails,
    updateVisitorGroupDetailsLoading
  };
};

export default useExternalUsers;
